import React from 'react';
import { motion } from 'framer-motion';
import { useDarkMode } from '../../../context/DarkModeContext';
import { 
  IoTrendingUpOutline, 
  IoTrendingDownOutline, 
  IoRemoveOutline,
  IoPencilOutline,
  IoTimeOutline
} from 'react-icons/io5';

interface MetricCardProps {
  metric: {
    id: string;
    metric_name: string;
    current_value: number;
    baseline_value?: number;
    target_value?: number;
    metric_unit?: string;
    category?: string;
    notes?: string;
    trend?: 'up' | 'down' | 'stable';
  };
  onEdit: () => void;
  onViewHistory?: () => void;
}

const MetricCard: React.FC<MetricCardProps> = ({ metric, onEdit, onViewHistory }) => {
  const { darkMode } = useDarkMode();

  // Calculate progress percentage if target exists
  const progressPercentage = metric.target_value 
    ? Math.min((metric.current_value / metric.target_value) * 100, 100)
    : null;

  // Calculate change from baseline
  const changeFromBaseline = metric.baseline_value 
    ? ((metric.current_value - metric.baseline_value) / metric.baseline_value) * 100
    : null;

  // Determine trend based on change
  const trend = changeFromBaseline !== null
    ? changeFromBaseline > 1 ? 'up' : changeFromBaseline < -1 ? 'down' : 'stable'
    : metric.trend || 'stable';

  // Style classes
  const cardBg = darkMode ? 'bg-gray-800' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const progressBg = darkMode ? 'bg-gray-700' : 'bg-gray-200';

  // Get category color
  const getCategoryColor = (category?: string) => {
    const colors: Record<string, string> = {
      productivity: darkMode ? 'text-blue-400' : 'text-blue-600',
      quality: darkMode ? 'text-green-400' : 'text-green-600',
      leadership: darkMode ? 'text-purple-400' : 'text-purple-600',
      impact: darkMode ? 'text-orange-400' : 'text-orange-600',
      collaboration: darkMode ? 'text-pink-400' : 'text-pink-600'
    };
    return colors[category || ''] || (darkMode ? 'text-gray-400' : 'text-gray-600');
  };

  // Get trend icon and color
  const getTrendIcon = () => {
    switch (trend) {
      case 'up':
        return <IoTrendingUpOutline className="w-5 h-5 text-green-500" />;
      case 'down':
        return <IoTrendingDownOutline className="w-5 h-5 text-red-500" />;
      default:
        return <IoRemoveOutline className="w-5 h-5 text-gray-500" />;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-5 rounded-xl border ${borderColor} ${cardBg} shadow-sm hover:shadow-md transition-shadow`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className={`text-lg font-semibold ${textColor} mb-1`}>
            {metric.metric_name}
          </h3>
          {metric.category && (
            <span className={`text-xs font-medium ${getCategoryColor(metric.category)} uppercase`}>
              {metric.category}
            </span>
          )}
        </div>
        <button
          onClick={onEdit}
          className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${mutedTextColor}`}
        >
          <IoPencilOutline className="w-4 h-4" />
        </button>
      </div>

      {/* Current Value */}
      <div className="mb-4">
        <div className="flex items-baseline gap-3">
          <span className={`text-3xl font-bold ${textColor}`}>
            {metric.current_value}
          </span>
          {metric.metric_unit && (
            <span className={`text-lg ${mutedTextColor}`}>
              {metric.metric_unit}
            </span>
          )}
          {getTrendIcon()}
        </div>
        
        {/* Change from baseline */}
        {changeFromBaseline !== null && (
          <p className={`text-sm ${mutedTextColor} mt-1`}>
            {changeFromBaseline > 0 ? '+' : ''}{changeFromBaseline.toFixed(1)}% from baseline
          </p>
        )}
      </div>

      {/* Progress Bar (if target exists) */}
      {metric.target_value && progressPercentage !== null && (
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-1">
            <span className={mutedTextColor}>Progress to target</span>
            <span className={`font-medium ${textColor}`}>
              {progressPercentage.toFixed(0)}%
            </span>
          </div>
          <div className={`h-2 rounded-full ${progressBg} overflow-hidden`}>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progressPercentage}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className={`h-full rounded-full ${
                progressPercentage >= 100 
                  ? 'bg-green-500' 
                  : progressPercentage >= 75 
                  ? 'bg-blue-500' 
                  : progressPercentage >= 50 
                  ? 'bg-yellow-500' 
                  : 'bg-red-500'
              }`}
            />
          </div>
          <div className="flex justify-between text-xs mt-1">
            {metric.baseline_value !== undefined && (
              <span className={mutedTextColor}>
                Baseline: {metric.baseline_value}
              </span>
            )}
            <span className={`${mutedTextColor} ml-auto`}>
              Target: {metric.target_value}
            </span>
          </div>
        </div>
      )}

      {/* Notes */}
      {metric.notes && (
        <p className={`text-sm ${mutedTextColor} italic mb-3`}>
          {metric.notes}
        </p>
      )}

      {/* View History Button */}
      {onViewHistory && (
        <button
          onClick={onViewHistory}
          className={`w-full mt-3 px-3 py-2 text-sm font-medium ${mutedTextColor} hover:${textColor} border ${borderColor} rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center justify-center gap-2`}
        >
          <IoTimeOutline className="w-4 h-4" />
          View History
        </button>
      )}
    </motion.div>
  );
};

export default MetricCard;